// src/components/SEO.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// Helper to ensure URLs are absolute
function absolute(url) {
  const siteUrl = 'https://oolook.in';
  if (!url) return siteUrl;
  return /^https?:\/\//i.test(url) ? url : `${siteUrl}${url.startsWith('/') ? '' : '/'}${url}`;
}

const SEO = ({
  title,
  description,
  image,
  noIndex = false,
  article, // Special prop for blog posts
  schemas = [], // Array for additional schemas
}) => {
  const { pathname } = useLocation();
  const canonicalUrl = absolute(pathname);
  const ogImage = absolute(image || '/og/default.png'); // Default OG image

  // --- Base Schemas ---
  const allSchemas = [];

  // Add the main Organization schema by default to non-article pages
  if (!article) {
    allSchemas.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Oolook",
      "url": "https://oolook.in",
      "logo": absolute("/logo512.png"),
    });
  }

  // Add any custom schemas passed in via props
  if (schemas && schemas.length > 0) {
    allSchemas.push(...schemas);
  }

  // --- Article Schema Logic ---
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title || title,
    "image": [ogImage],
    "datePublished": article.publishedTime,
    "dateModified": article.modifiedTime || article.publishedTime,
    "author": [{ "@type": "Person", "name": article.authorName }],
    "publisher": {
      "@type": "Organization",
      "name": "Oolook",
      "logo": {
        "@type": "ImageObject",
        "url": absolute("/logo512.png"),
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    ...(article.tags && { "keywords": article.tags.join(', ') }),
    ...(article.section && { "articleSection": article.section }),
  } : null;

  if (articleSchema) {
    allSchemas.push(articleSchema);
  }

  return (
    <Helmet>
      {/* --- Primary Meta Tags --- */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, follow" />}

      {/* --- Open Graph / Facebook --- */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Oolook" />

      {/* --- Twitter --- */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* --- Render all JSON-LD Schemas --- */}
      {allSchemas.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string,
  noIndex: PropTypes.bool,
  article: PropTypes.shape({
    title: PropTypes.string,
    publishedTime: PropTypes.string.isRequired,
    modifiedTime: PropTypes.string,
    authorName: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    section: PropTypes.string,
  }),
  schemas: PropTypes.array,
};

export default SEO;